// Modal navigation utilities - handles navigation between workflows in the processing queue

import { switchWorkflowSession } from '../store/reducers';
import { loadWorkflowLastJobId } from './workflowSessionStorage';

// Get queue navigation info for a workflow - (params: {workflowSessionId: string, workflowSessions: Object}) -> {position, total, hasNext, hasPrev, nextWorkflowId, prevWorkflowId}
export const getQueueNavigationInfo = ({
  workflowSessionId,
  workflowSessions,
}) => {
  const currentlyProcessingId = workflowSessions?.currentlyProcessingSessionId;
  const queue = workflowSessions?.processingQueue || [];
  const sessionOrder = workflowSessions?.sessionOrder || [];
  
  // Get all workflows that are actually processing or in queue (not just those with documents). Include: currently processing, in queue, or have jobId. Exclude workflows with only documents but not queued/started
  const allProcessingWorkflows = [];
  const sessions = workflowSessions?.sessions || {};
  
  // Add currently processing workflow first
  if (currentlyProcessingId) {
    allProcessingWorkflows.push(currentlyProcessingId);
  }
  
  // Add workflows in queue (these are waiting to process)
  queue.forEach(id => {
    if (!allProcessingWorkflows.includes(id)) {
      allProcessingWorkflows.push(id);
    }
  });
  
  // Also include workflows that are: processing, have jobId, or have completed steps (to allow navigation to completed workflows)
  Object.keys(sessions).forEach(id => {
    if (!allProcessingWorkflows.includes(id)) {
      const session = sessions[id];
      const hasJobId = !!loadWorkflowLastJobId(id);
      const isProcessing = session?.state?.isProcessing || false;
      const isCompleted = session?.state?.stepsCompleted || false;
      
      // Include if processing, has jobId, or completed (allows navigation to completed workflows)
      if (isProcessing || hasJobId || isCompleted) {
        allProcessingWorkflows.push(id);
      }
    }
  });
  
  // Ensure current workflow is included if it's processing, has jobId, or is completed
  if (workflowSessionId && !allProcessingWorkflows.includes(workflowSessionId)) {
    const session = sessions[workflowSessionId];
    const hasJobId = !!loadWorkflowLastJobId(workflowSessionId);
    const isProcessing = session?.state?.isProcessing || false;
    const isCompleted = session?.state?.stepsCompleted || false;
    
    // Include if it's processing, has a jobId, or is completed
    if (isProcessing || hasJobId || isCompleted) {
      // Add at the beginning if it's the currently processing one
      if (workflowSessionId === currentlyProcessingId) {
        allProcessingWorkflows.unshift(workflowSessionId);
      } else {
        allProcessingWorkflows.push(workflowSessionId);
      }
    }
  }
  
  const currentIndex = allProcessingWorkflows.indexOf(workflowSessionId);
  const total = allProcessingWorkflows.length;
  const position = currentIndex >= 0 ? currentIndex + 1 : null;
  
  const nextIndex = currentIndex >= 0 && currentIndex < total - 1 ? currentIndex + 1 : -1;
  const prevIndex = currentIndex > 0 ? currentIndex - 1 : -1;
  
  const result = {
    position,
    total,
    hasNext: nextIndex >= 0,
    hasPrev: prevIndex >= 0,
    nextWorkflowId: nextIndex >= 0 ? allProcessingWorkflows[nextIndex] : null,
    prevWorkflowId: prevIndex >= 0 ? allProcessingWorkflows[prevIndex] : null,
    allProcessingWorkflows,
    currentIndex,
  };
  
  // Removed console.log that was causing infinite loops on every render/stream update Uncomment only for debugging specific issues console.log(`[ModalNavigation] getQueueNavigationInfo for ${workflowSessionId}:`, { ...result, currentlyProcessingId, queue, allProcessingWorkflowsCount: allProcessingWorkflows.length });
  
  return result;
};

// Navigate to next workflow in queue - (params: {navigationInfo: Object, dispatch: Function})
export const navigateToNextWorkflow = ({
  navigationInfo,
  dispatch,
}) => {
  console.log(`[ModalNavigation] navigateToNextWorkflow called:`, {
    hasNext: navigationInfo.hasNext,
    nextWorkflowId: navigationInfo.nextWorkflowId,
    currentPosition: navigationInfo.position,
    total: navigationInfo.total,
    allWorkflows: navigationInfo.allProcessingWorkflows
  });
  
  if (!navigationInfo.hasNext || !navigationInfo.nextWorkflowId) {
    console.warn(`[ModalNavigation] Cannot navigate next: hasNext=${navigationInfo.hasNext}, nextWorkflowId=${navigationInfo.nextWorkflowId}`);
    return;
  }
  
  console.log(`[ModalNavigation] Dispatching switchWorkflowSession to: ${navigationInfo.nextWorkflowId}`);
  dispatch(switchWorkflowSession(navigationInfo.nextWorkflowId));
};

// Navigate to previous workflow in queue - (params: {navigationInfo: Object, dispatch: Function})
export const navigateToPrevWorkflow = ({
  navigationInfo,
  dispatch,
}) => {
  console.log(`[ModalNavigation] navigateToPrevWorkflow called:`, {
    hasPrev: navigationInfo.hasPrev,
    prevWorkflowId: navigationInfo.prevWorkflowId,
    currentPosition: navigationInfo.position,
    total: navigationInfo.total,
    allWorkflows: navigationInfo.allProcessingWorkflows
  });
  
  if (!navigationInfo.hasPrev || !navigationInfo.prevWorkflowId) {
    console.warn(`[ModalNavigation] Cannot navigate prev: hasPrev=${navigationInfo.hasPrev}, prevWorkflowId=${navigationInfo.prevWorkflowId}`);
    return;
  }
  
  console.log(`[ModalNavigation] Dispatching switchWorkflowSession to: ${navigationInfo.prevWorkflowId}`);
  dispatch(switchWorkflowSession(navigationInfo.prevWorkflowId));
};

